import moment from 'moment';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { numericFormatter } from 'react-number-format';
import { useQuery } from 'react-query';
import 'moment/locale/uz-latn';
import 'moment/locale/ru';
import 'moment/locale/uz';
import { Button, CircularProgress } from '@mui/material';

import { colors } from 'shared/colors';
import ObjectSelectBox from 'components/boss/ObjectSelectBox';
import BarChart from 'components/ui/charts/BarChart';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import useCurrency from 'hooks/useCurrency';
import { arrearsBarTooltip } from 'utils/chartjsTooltips';
import InfoModal from './InfoModal';

const Arrears = () => {
  const { t, i18n } = useTranslation();
  const { setComponent } = useTopPanel();
  const axiosPrivate = useAxiosPrivate();
  const { currencyData } = useCurrency();
  const [debtorsData, setDebtorsData] = useState([]);
  const [selectedObjectId, setSelectedObjectId] = useState('ALL');
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState({});

  const [startAndFinishDates, setStartAndFinishDates] = useState({
    startDate: moment().startOf('months').format('YYYY-MM-DD'),
    finishDate: moment().endOf('months').format('YYYY-MM-DD'),
  });
  const localeMoment = moment;

  const { isLoading, isFetching, isError } = useQuery({
    queryKey: ['analyticDebtors', startAndFinishDates, selectedObjectId],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/analytic/debitors/main?type='DAILY'&start=${
          startAndFinishDates.startDate
        }&finish=${startAndFinishDates.finishDate}${
          selectedObjectId != 'ALL' ? `&objects[0]=${selectedObjectId}` : ''
        }`
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      if (data && data.length > 0) {
        const list = data.map((item) => ({
          date: item?.date,
          objects: item?.objects?.length
            ? item?.objects?.map((item) => ({
                ...item,
                total: +item?.uzs + currencyData?.sum * +item?.usd,
              }))
            : [],
          sum: item?.objects?.length
            ? item?.objects?.reduce((a, b) => a + +b?.uzs, 0)
            : 0,
          usd: item?.objects?.length
            ? item?.objects?.reduce(
                (a, b) => a + +b?.usd * currencyData?.sum,
                0
              )
            : 0,
        }));
        setDebtorsData(list);
      }
    },
    // enabled: !hasError,
    // onError: (error) => {
    // 	setHasError(true)
    // },
    retry: false,
  });

  const handlePrevMonth = () => {
    const newStartDate = moment(startAndFinishDates.startDate, 'YYYY-MM-DD')
      .subtract(1, 'M')
      .startOf('M')
      .format('YYYY-MM-DD');

    const newFinishDate = moment(startAndFinishDates.finishDate, 'YYYY-MM-DD')
      .subtract(1, 'M')
      .endOf('M')
      .format('YYYY-MM-DD');

    setStartAndFinishDates({
      startDate: newStartDate,
      finishDate: newFinishDate,
    });
  };

  const handleNextMonth = () => {
    const newStartDate = moment(startAndFinishDates.startDate, 'YYYY-MM-DD')
      .add(1, 'M')
      .startOf('M')
      .format('YYYY-MM-DD');

    const newFinishDate = moment(startAndFinishDates.finishDate, 'YYYY-MM-DD')
      .add(1, 'M')
      .endOf('M')
      .format('YYYY-MM-DD');

    setStartAndFinishDates({
      startDate: newStartDate,
      finishDate: newFinishDate,
    });
  };
  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('boss.arrears.title')}</div>
    );
    localeMoment.locale(i18n.language == 'uz' ? 'uz-latn' : 'uz');
  }, [i18n]);

  return (
    <div className='component-wrapper boss-arrears-wrapper'>
      <div className='arrears-actions-wrapper'>
        <div className='arrears-actions-body'>
          <div className='flex items-center'>
            <div className='w-full ml-4'>
              <ObjectSelectBox
                value={selectedObjectId}
                setValue={setSelectedObjectId}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='arrears-chart !shadow-md !base-border'>
        <div className='chart-header'>
          <Button
            variant='action'
            color='secondary'
            onClick={() => handlePrevMonth()}
            disabled={isLoading || isFetching}
          >
            <i className='bi bi-chevron-left' />
          </Button>
          <div className='chart-title'>
            {`${localeMoment(
              startAndFinishDates.startDate,
              'YYYY-MM-DD'
            ).format('MMMM')}, `}
            {localeMoment(startAndFinishDates.startDate, 'YYYY-MM-DD').year()}
          </div>
          <Button
            variant='action'
            color='secondary'
            onClick={() => handleNextMonth()}
            disabled={isLoading || isFetching}
          >
            <i className='bi bi-chevron-right' />
          </Button>
        </div>
        {isError ? (
          <div>
            <span className='no-data-found-wrapper'>
              <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
              {t('common.global.noDataFound')}
            </span>
          </div>
        ) : debtorsData.length > 0 ? (
          <div className='h-[500px]'>
            <BarChart
              chartLabels={debtorsData?.map((item) =>
                localeMoment(item.date, 'YYYY-MM-DD').format('DD')
              )}
              onClick={(props) => {
                const index = props?.[0]?.index;
                const data = debtorsData?.[index];
                setOpenModal(true);
                setModalData(data);
              }}
              chartDatasets={[
                {
                  data: [...debtorsData].map((item) => ({
                    total:
                      +item.sum +
                      (item?.usd ? +item.usd * currencyData?.sum : 0),
                    sum: item?.sum || 0,
                    objects: item?.objects || [],
                    usd: item?.usd || 0,
                  })),
                  borderRadius: 4,
                  borderWidth: 2,
                  borderColor: colors.baseColorLight,
                  backgroundColor: colors.baseColorOutline,
                  hoverBackgroundColor: colors.baseColorLight,
                  tension: 0.3,
                },
              ]}
              optionPlugins={{
                tooltip: {
                  enabled: false,
                  external: arrearsBarTooltip('sum', 'usd', 'total', 'objects'),
                },
              }}
              options={{
                scales: {
                  y: {
                    ticks: {
                      callback: function (value) {
                        let isTrillion =
                          parseFloat(value) / Math.pow(10, 12) >= 1;
                        let isBillion =
                          parseFloat(value) / Math.pow(10, 9) >= 1;
                        return Math.floor(value) > 1
                          ? numericFormatter(
                              isTrillion
                                ? (
                                    parseFloat(value) / Math.pow(10, 12)
                                  ).toString()
                                : isBillion
                                ? (
                                    parseFloat(value) / Math.pow(10, 9)
                                  ).toString()
                                : (
                                    parseFloat(value) / Math.pow(10, 6)
                                  ).toString(),
                              {
                                decimalScale: 3,
                                thousandSeparator: ' ',
                                allowNegative: false,
                                suffix: isTrillion
                                  ? ' trln UZS'
                                  : isBillion
                                  ? ' mlrd UZS'
                                  : ' mln UZS',
                              }
                            )
                          : value;
                      },
                    },
                  },
                },
                parsing: {
                  xAxisKey: 'total',
                  yAxisKey: 'total',
                },
              }}
            />
          </div>
        ) : (
          <div className='h-[300px] flex items-center justify-center'>
            <CircularProgress size={30} color='inherit' />
          </div>
        )}
      </div>
      <div className='arrears-table'></div>
      <InfoModal data={modalData} open={openModal} setOpen={setOpenModal} />
    </div>
  );
};
export default memo(Arrears);
